import { useMemo, useRef, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import StatusBadge from '@/components/base/StatusBadge';
import FilterSelect, { type FilterOption } from '@/components/base/FilterSelect';
import PlatformIcon from '@/components/base/PlatformIcon';
import { formatDate } from '@/utils/format';
import { groupOf, allChecksPass, type DeliverableGroup } from '@/utils/deliverable';
import type { CampaignDeliverable } from '@/types/portal';
import type { DeliverableReviewState } from '@/hooks/useDeliverableReviews';
import DeliverableReviewModal from './DeliverableReviewModal';

interface DeliverablesPanelProps {
  deliverables: CampaignDeliverable[];
  state: DeliverableReviewState;
  /** False for roles without `campaigns.approve_content`. */
  canReview?: boolean;
}

const FILTER_LABELS: Record<DeliverableGroup, string> = {
  awaiting: 'Awaiting review',
  changes: 'Changes requested',
  approved: 'Approved',
  draft: 'Not submitted',
  exception: 'Needs attention',
};

const SUMMARY_TILES: { group: DeliverableGroup; label: string; icon: string; tone: string }[] = [
  { group: 'awaiting', label: 'Awaiting review', icon: 'ri-eye-line', tone: 'text-secondary-700' },
  { group: 'changes', label: 'Changes requested', icon: 'ri-edit-2-line', tone: 'text-primary-700' },
  { group: 'approved', label: 'Approved', icon: 'ri-checkbox-circle-line', tone: 'text-accent-700' },
  { group: 'draft', label: 'Not submitted', icon: 'ri-draft-line', tone: 'text-foreground-500' },
];

export default function DeliverablesPanel({
  deliverables,
  state,
  canReview = true,
}: DeliverablesPanelProps) {
  const [filter, setFilter] = useState('all');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const groups = useMemo(() => {
    const counts: Record<DeliverableGroup, number> = {
      awaiting: 0,
      changes: 0,
      approved: 0,
      draft: 0,
      exception: 0,
    };
    deliverables.forEach((item) => {
      counts[groupOf(state.statusOf(item.id))] += 1;
    });
    return counts;
  }, [deliverables, state]);

  const filterOptions: FilterOption[] = useMemo(() => {
    const options: FilterOption[] = [
      { id: 'all', label: 'All deliverables', count: deliverables.length, icon: 'ri-list-check' },
    ];
    (Object.keys(FILTER_LABELS) as DeliverableGroup[]).forEach((group) => {
      if (group === 'exception' || groups[group] > 0) {
        options.push({
          id: group,
          label: FILTER_LABELS[group],
          count: groups[group],
          icon: 'ri-filter-3-line',
        });
      }
    });
    return options;
  }, [deliverables.length, groups]);

  const visible = useMemo(() => {
    if (filter === 'all') return deliverables;
    return deliverables.filter((item) => groupOf(state.statusOf(item.id)) === filter);
  }, [deliverables, filter, state]);

  const active = deliverables.find((item) => item.id === activeId) ?? null;

  // Deliverables that are eligible for bulk-approval:
  // have a submitted review, every check passes, and are not already approved/paid.
  const bulkEligible = useMemo(
    () =>
      deliverables.filter((item) => {
        const review = state.reviewOf(item.id);
        const status = state.statusOf(item.id);
        if (!review) return false;
        if (status === 'Approved' || status === 'Paid') return false;
        return allChecksPass(review.checks);
      }),
    [deliverables, state],
  );

  const openConfirm = () => {
    setBulkNote('');
    setConfirmOpen(true);
    setTimeout(() => noteRef.current?.focus(), 50);
  };

  const handleBulkApprove = async () => {
    await state.approveMany(bulkEligible, bulkNote);
    setConfirmOpen(false);
    setBulkNote('');
  };

  if (deliverables.length === 0) {
    return (
      <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
          <i className="ri-movie-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
          No deliverables yet
        </h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Deliverables appear here once creators accept and contracts are signed.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-5">
      {state.error && !active ? (
        <div
          role="alert"
          className="mb-4 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800"
        >
          <span className="inline-flex items-center gap-2">
            <i className="ri-error-warning-line" />
            {state.error}
          </span>
          <button
            type="button"
            onClick={state.clearError}
            className="cursor-pointer text-primary-700 hover:text-primary-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {SUMMARY_TILES.map((tile) => (
          <div key={tile.group} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <p className="flex items-center gap-1.5 text-xs text-foreground-500">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={tile.icon} />
              </span>
              {tile.label}
            </p>
            <p className={`mt-1 font-heading text-xl font-semibold ${tile.tone}`}>
              {groups[tile.group]}
            </p>
          </div>
        ))}
      </div>

      {groups.exception > 0 ? (
        <p className="mt-3 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-800">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-alert-line" />
          </span>
          {groups.exception} deliverable{groups.exception === 1 ? '' : 's'} flagged as late or
          missing — chase the creator or extend the deadline.
        </p>
      ) : null}

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-heading text-sm font-semibold text-foreground-950">
            Review queue
          </h2>
          {canReview && bulkEligible.length > 0 ? (
            <button
              type="button"
              onClick={openConfirm}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-accent-300 bg-accent-50 px-2.5 py-0.5 text-[11px] font-label font-semibold text-accent-800 transition-colors hover:bg-accent-100"
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-checkbox-multiple-line" />
              </span>
              Approve all passing ({bulkEligible.length})
            </button>
          ) : null}
        </div>
        <FilterSelect value={filter} options={filterOptions} onChange={setFilter} label="Show" />
      </div>

      <div className="mt-3 space-y-3">
        {visible.map((item) => {
          const status = state.statusOf(item.id);
          const review = state.reviewOf(item.id);
          const thread = state.threadOf(item.id);
          const latest = thread.length > 0 ? thread[thread.length - 1] : null;
          const reviewable = Boolean(review);

          return (
            <div
              key={item.id}
              className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100">
                  {review ? (
                    <img
                      src={review.mediaUrl}
                      loading="lazy"
                      decoding="async"
                      alt={`${item.type} preview`}
                      title={`${item.type} — ${item.platform}`}
                      className="h-full w-full object-cover object-top"
                    />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center text-lg text-foreground-400">
                      <i className="ri-file-video-line" />
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-950">{item.type}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1.5">
                      <Avatar src={item.avatar} alt={item.creatorName} size="xs" />
                      {item.creatorName}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="text-foreground-300">·</span>
                      <PlatformIcon platform={item.platform} className="text-[12px]" />
                      {item.platform}
                    </span>
                    <span>· due {formatDate(item.dueDate)}</span>
                  </div>
                  {latest && latest.decision !== 'approved' ? (
                    <p className="mt-1 flex items-start gap-1.5 text-xs text-foreground-600">
                      <span className="mt-0.5 w-3.5 h-3.5 flex items-center justify-center shrink-0 text-secondary-600">
                        <i className="ri-chat-3-line" />
                      </span>
                      <span className="line-clamp-1">
                        {latest.author}: {latest.text}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                <StatusBadge status={status} />
                <Button
                  size="sm"
                  variant={reviewable ? 'primary' : 'outline'}
                  icon={reviewable ? 'ri-eye-line' : 'ri-time-line'}
                  disabled={!reviewable}
                  onClick={() => setActiveId(item.id)}
                >
                  {reviewable ? 'Review' : 'Awaiting submission'}
                </Button>
              </div>
            </div>
          );
        })}
        {visible.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-10 text-center text-sm text-foreground-500">
            No deliverables match this filter.
          </div>
        ) : null}
      </div>

      <DeliverableReviewModal
        deliverable={active}
        review={active ? state.reviewOf(active.id) : undefined}
        status={active ? state.statusOf(active.id) : 'Draft'}
        thread={active ? state.threadOf(active.id) : []}
        onClose={() => {
          setActiveId(null);
          state.clearError();
        }}
        onDecide={(decision, text, timecode) => {
          if (active) void state.decide(active, decision, text, timecode);
        }}
        canReview={canReview}
        error={state.error}
      />

      {/* Bulk-approve confirmation dialog */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        icon="ri-checkbox-multiple-line"
        title="Approve all passing deliverables"
        subtitle={`${bulkEligible.length} deliverable${bulkEligible.length === 1 ? '' : 's'} passed every brief check and will be marked Approved.`}
        widthClassName="max-w-lg"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="accent"
              size="sm"
              icon="ri-checkbox-multiple-line"
              onClick={handleBulkApprove}
              disabled={state.saving}
            >
              Approve {bulkEligible.length}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* List the pieces being approved */}
          <ul className="divide-y divide-background-200 rounded-lg border border-background-200 bg-background-100">
            {bulkEligible.map((item) => {
              const review = state.reviewOf(item.id);
              return (
                <li key={item.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="h-10 w-14 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-50">
                    {review ? (
                      <img
                        src={review.mediaUrl}
                        loading="lazy"
                        decoding="async"
                        alt={item.type}
                        className="h-full w-full object-cover object-top"
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-foreground-400">
                        <i className="ri-file-video-line" />
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground-900">{item.type}</p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-foreground-500">
                      <Avatar src={item.avatar} alt={item.creatorName} size="xs" />
                      {item.creatorName} · {item.platform}
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 text-[11px] font-label font-semibold text-accent-700">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-shield-check-line" />
                    </span>
                    All checks pass
                  </span>
                </li>
              );
            })}
          </ul>

          {/* Optional shared note */}
          <div>
            <label
              htmlFor="bulk-approve-note"
              className="text-xs font-label font-semibold uppercase tracking-wider text-foreground-400"
            >
              Approval note (optional)
            </label>
            <textarea
              id="bulk-approve-note"
              ref={noteRef}
              value={bulkNote}
              maxLength={500}
              onChange={(event) => setBulkNote(event.target.value)}
              rows={2}
              placeholder="Add a shared note to all approved pieces..."
              className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <p className="mt-0.5 text-right text-[11px] text-foreground-400">{bulkNote.length}/500</p>
          </div>

          <p className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-xs text-foreground-600">
            <span className="mt-0.5 w-4 h-4 flex items-center justify-center shrink-0">
              <i className="ri-information-line" />
            </span>
            Pieces with a warning or a failed check are not included. Review those individually before approving.
          </p>
        </div>
      </Modal>
    </div>
  );
}